import { ReactElement } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BusinessOutlined, Close, EmailOutlined } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isValidPhoneNumber } from 'libphonenumber-js';
import Alert from 'shared/components/alert/alert';
import DateRangePicker from 'shared/components/date-range-picker/date-range-picker';
import MultiSelectField from 'shared/components/formFields/multi-select-field';
import SelectField from 'shared/components/formFields/select-field';
import SonevoLoadingIndicator from 'shared/components/loading-indicator/sonevo-loader';
import { RegexExpressions } from 'shared/constants/regex/regex-expressions';

import useTemporaryUserDetailsPopup from 'features/temporary-access/views/temporary-user-details-popup/hooks';
import { useTemporaryAccessContextProvider } from '../../providers/temporary-access-provider';

import './temporary-user-details-popup.component.scss';

const TemporaryUserDetailsPopup = (): ReactElement => {
  const { t } = useTranslation('temporaryAccess');

  const {
    temporaryUserDetailsPopupIsOpen,
    createTemporaryUserIsLoading,
    createTemporaryUserIsSuccess,
    createTemporaryUserIsError,
    readOrganisationUnitsForTemporaryAccessData,
    readOrganisationUnitsForTemporaryAccessIsSuccess,
    readRolesForTemporaryAccessData,
    readRolesForTemporaryAccessIsSuccess,
    readTemporaryUserIsFetching,
    detailsViewingMode,
  } = useTemporaryAccessContextProvider();

  const { form, validatePeriod, submit, resetFormAndCloseTemporaryUserDetailsPopup } = useTemporaryUserDetailsPopup();

  const control = form.control;
  const errors = form.formState.errors;

  const registerOptions = {
    firstName: {
      required: t('detailsPopup.body.form.details.firstNameInputRequiredHelperText'),
    },
    lastName: {
      required: t('detailsPopup.body.form.details.lastNameInputRequiredHelperText'),
    },
    email: {
      required: t('detailsPopup.body.form.details.emailInputRequiredHelperText'),
      validate: (value: string): boolean | string => {
        const regex = new RegExp(RegexExpressions.EMAIL);
        if (form.getValues().period) {
          form.trigger('period');
        }
        return regex.test(value?.trim()) || t('detailsPopup.body.form.details.emailInputInvalidHelperText');
      },
    },
    phone: {
      required: t('detailsPopup.body.form.details.phoneInputRequiredHelperText'),
      validate: (value: string): boolean | string =>
        isValidPhoneNumber(value.trim(), 'NL') || t('detailsPopup.body.form.details.phoneInputInvalidHelperText'),
    },
    organisationUnits: {
      required: t('detailsPopup.body.form.access.organisationUnitInputRequiredHelperText'),
      validate: (): boolean | string => {
        if (form.getValues().period) {
          form.trigger('period');
        }
        return true;
      },
    },
    role: {
      required: t('detailsPopup.body.form.access.roleInputRequiredHelperText'),
    },
    period: {
      required: t('detailsPopup.body.form.period.periodInputRequiredHelperText'),
      validate: validatePeriod,
    },
  };

  return (
    <Dialog
      open={temporaryUserDetailsPopupIsOpen}
      onClose={resetFormAndCloseTemporaryUserDetailsPopup}
      slotProps={{
        paper: { classes: { root: 'temporary-user-details-popup-paper' } },
      }}
      maxWidth="xl"
    >
      <FormProvider {...form}>
        <div className="temporary-user-details-container">
          {detailsViewingMode === 'viewing' && readTemporaryUserIsFetching ? (
            <div className="loading-indicator">
              <SonevoLoadingIndicator size={50} />
            </div>
          ) : (
            <>
              <div className="temporary-user-details-header-container">
                <Typography variant="h3">{t('detailsPopup.header.title')}</Typography>
                <IconButton aria-label="close" className="close-button" onClick={resetFormAndCloseTemporaryUserDetailsPopup}>
                  <Close />
                </IconButton>
              </div>
              <Divider className="temporary-user-details-header-divider" />
              <div className={`temporary-user-details-body-container ${detailsViewingMode === 'viewing' ? 'viewing-mode-padding' : ''}`}>
                <form onSubmit={form.handleSubmit(submit)} className="temporary-user-details-form-container" data-testid="detailPopupForm">
                  <div className="temporary-user-details-form-group">
                    <Typography variant="h5">{t('detailsPopup.body.form.details.title')}</Typography>
                    <Controller
                      name="firstName"
                      rules={registerOptions.firstName}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="firstNameInput"
                          label={t('detailsPopup.body.form.details.firstNameInput')}
                          variant="outlined"
                          error={!!errors.firstName}
                          helperText={errors.firstName?.message?.toString()}
                          disabled={detailsViewingMode === 'viewing'}
                        />
                      )}
                    />
                    <Controller
                      name="lastName"
                      rules={registerOptions.lastName}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="lastNameInput"
                          label={t('detailsPopup.body.form.details.lastNameInput')}
                          variant="outlined"
                          error={!!errors.lastName}
                          helperText={errors.lastName?.message?.toString()}
                          disabled={detailsViewingMode === 'viewing'}
                        />
                      )}
                    />
                    <Controller
                      name="email"
                      rules={registerOptions.email}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="emailInput"
                          label={t('detailsPopup.body.form.details.emailInput')}
                          variant="outlined"
                          error={!!errors.email}
                          helperText={errors.email?.message?.toString()}
                          disabled={detailsViewingMode === 'viewing'}
                        />
                      )}
                    />
                    <Controller
                      name="phone"
                      rules={registerOptions.phone}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="phoneInput"
                          label={t('detailsPopup.body.form.details.phoneInput')}
                          variant="outlined"
                          error={!!errors.phone}
                          helperText={errors.phone?.message?.toString()}
                          disabled={detailsViewingMode === 'viewing'}
                        />
                      )}
                    />
                  </div>
                  <div className="temporary-user-details-form-group">
                    <Typography variant="h5">{t('detailsPopup.body.form.access.title')}</Typography>
                    <Controller
                      name="organisationUnits"
                      control={control}
                      rules={registerOptions.organisationUnits}
                      render={({ field }) => (
                        <MultiSelectField
                          label={t('detailsPopup.body.form.access.organisationUnitInput')}
                          options={
                            readOrganisationUnitsForTemporaryAccessData && readOrganisationUnitsForTemporaryAccessIsSuccess
                              ? readOrganisationUnitsForTemporaryAccessData.map((organisationUnit) => ({
                                  key: organisationUnit.id,
                                  value: organisationUnit.name,
                                }))
                              : []
                          }
                          field={field}
                          error={!!errors.organisationUnits}
                          helperText={errors?.organisationUnits?.message?.toString()}
                          disabled={detailsViewingMode === 'viewing'}
                          startAdornment={
                            <InputAdornment position="start">
                              <BusinessOutlined fontSize="small" />
                            </InputAdornment>
                          }
                        />
                      )}
                    />
                    <Controller
                      name="role"
                      control={control}
                      rules={registerOptions.role}
                      render={({ field }) => (
                        <SelectField
                          {...field}
                          id="roleInput"
                          label={t('detailsPopup.body.form.access.roleInput')}
                          variant="outlined"
                          error={!!errors.role}
                          helperText={errors?.role?.message?.toString()}
                          onClick={() => form.trigger('role')}
                          disabled={detailsViewingMode === 'viewing'}
                        >
                          {readRolesForTemporaryAccessData &&
                            readRolesForTemporaryAccessIsSuccess &&
                            readRolesForTemporaryAccessData.map((role) => (
                              <MenuItem key={`role-${role.id}`} value={role.idpId}>
                                {role.name}
                              </MenuItem>
                            ))}
                        </SelectField>
                      )}
                    />
                  </div>
                  <div className="temporary-user-details-form-group">
                    <Typography variant="h5">{t('detailsPopup.body.form.period.title')}</Typography>
                    <Controller
                      name="period"
                      control={control}
                      rules={registerOptions.period}
                      render={({ field }) => (
                        <DateRangePicker
                          {...field}
                          label={t('detailsPopup.body.form.period.periodInput')}
                          placeholder={t('detailsPopup.body.form.period.periodInput')}
                          error={!!errors.period}
                          helperText={errors.period?.message?.toString()}
                          name="period"
                          maxRangeInDays={7}
                          disablePast={true}
                          disabled={detailsViewingMode === 'viewing'}
                        />
                      )}
                    />
                  </div>
                  {detailsViewingMode === 'creation' && (
                    <Button
                      data-testid="submitButton"
                      startIcon={<EmailOutlined />}
                      variant="contained"
                      type="submit"
                      loading={createTemporaryUserIsLoading && !(createTemporaryUserIsError || createTemporaryUserIsSuccess)}
                      loadingPosition="start"
                    >
                      {t('detailsPopup.body.form.submitButtonText')}
                    </Button>
                  )}
                </form>
                <Alert title={t('detailsPopup.body.explanation.title')} body={t('detailsPopup.body.explanation.text')} />
              </div>
            </>
          )}
        </div>
      </FormProvider>
    </Dialog>
  );
};

export default TemporaryUserDetailsPopup;
